import { Link, useNavigate } from 'react-router-dom';
import DropdownUser from './DropdownUser';
import Logo from '../../images/logo/logo.svg';
import AsyncSelect from 'react-select/async';
import { fetchGlobal } from '~/services/global-search';
import GlobalSearchInfo from './GlobalSearchInfo';
import { useAuth } from '~/context/AuthProvider';

// Campaign statuses worth calling out in a search result, so an archived or
// closed campaign is distinguishable from an open one with the same name.
const FLAGGED_CAMPAIGN_STATUS_CODES = ['closed', 'inactive', 'archived'];

const Header = (props: {
    sidebarOpen: string | boolean | undefined;
    setSidebarOpen: (arg0: boolean) => void;
}) => {
    const Navigate = useNavigate();
    const { role } = useAuth();
    // Med pros don't have access to the records the global search returns;
    // hiding it for them avoids surfacing campaigns/patients they shouldn't
    // navigate into.
    const showGlobalSearch = role !== 'medical professional';

    function customDebounce<T extends (...args: any[]) => Promise<any>>(
        func: T,
        wait: number
    ): (...args: Parameters<T>) => Promise<ReturnType<T>> {
        let timeout: NodeJS.Timeout | null = null;
        let callId = 0;

        return (...args: Parameters<T>): Promise<ReturnType<T>> => {
            if (timeout) {
                clearTimeout(timeout);
            }

            const thisCallId = ++callId;

            return new Promise((resolve, reject) => {
                timeout = setTimeout(async () => {
                    try {
                        const result = await func(...args);
                        // Only resolve if this is still the latest call
                        if (thisCallId === callId) {
                            resolve(result);
                        } else {
                            resolve([] as any);
                        }
                    } catch (error) {
                        if (thisCallId === callId) {
                            reject(error);
                        } else {
                            resolve([] as any);
                        }
                    }
                }, wait);
            });
        };
    }

    // Debounced fetch function (only called after validation passes)
    const debouncedFetch = customDebounce(async (inputValue: string) => {
        // Check if it's an id: search
        if (inputValue.trim().toLowerCase().startsWith('id:')) {
            const campaignId = inputValue.split(':')[1].trim();
            if (campaignId && /^\d+$/.test(campaignId)) {
                return [
                    {
                        value: `/campaigns/${campaignId}`,
                        label: `Campaign ID: ${campaignId}`,
                    },
                ];
            }
            return [];
        }

        try {
            const results = await fetchGlobal(inputValue);
            const isCampaignRequestSearch = inputValue.trim().startsWith('cr:');
            return results.data.map((item: any) => {
                // For team members, show patient name in parentheses instead of the type
                const isTeamMember =
                    item.type === 'team_member' ||
                    item.type?.includes('team member');

                let suffix: string;
                if (isTeamMember && item.patient_name) {
                    suffix = item.patient_name;
                } else if (item.type === 'campaign') {
                    // Show state abbreviation / medical issue for campaigns
                    const parts = [item.state_abbreviation, item.medical_issue].filter(Boolean);
                    const statusLabel = FLAGGED_CAMPAIGN_STATUS_CODES.includes(item.campaign_status_code)
                        ? item.campaign_status_name || item.campaign_status_code
                        : '';
                    suffix =
                        [parts.join('/'), statusLabel].filter(Boolean).join(' - ') ||
                        item.type;
                } else if (item.type === 'campaign_request') {
                    // Name the initial contact when it isn't the patient; outside a cr: search,
                    // also say it's a campaign request so it isn't mistaken for a person row
                    const initialContact = item.initial_contact_name?.trim();
                    const contactSuffix =
                        initialContact && initialContact !== item.title?.trim() ? initialContact : '';
                    suffix = isCampaignRequestSearch
                        ? contactSuffix || item.type
                        : ['campaign request', contactSuffix].filter(Boolean).join(' - ');
                } else {
                    suffix = item.type;
                }

                return {
                    value: getRouteByType(item.type, item.id, item.user_id),
                    label: `${item.title} (${suffix})`,
                };
            });
        } catch (error) {
            console.error('Error fetching search results:', error);
            return [];
        }
    }, 500);

    // Wrapper that validates before triggering async load (avoids loading state for invalid input)
    const loadOptions = (inputValue: string) => {
        if (!isValidSearchString(inputValue)) {
            return Promise.resolve([]);
        }
        return debouncedFetch(inputValue);
    };

    function isValidSearchString(input: string) {
        const MIN_SEARCH_LENGTH = 3;

        if (input.includes(':')) {
            // For prefixed searches, extract and validate the search term after the prefix
            const withColonPattern =
                /\b(em|tm|ph|cr|c|u|n|mp|mc|note|lp|np|st|id):\s*([\w][\w\s'.@\-\u2018\u2019]*)?/;
            const match = input.match(withColonPattern);
            if (!match) return false;
            // Get the search term (group 2), trim it, and check length
            const searchTerm = (match[2] || '').trim();
            return searchTerm.length >= MIN_SEARCH_LENGTH;
        } else {
            // For non-prefixed searches, require valid characters and minimum length
            const withoutColonPattern = /^[\w\s'.\-\u2018\u2019]+$/;
            const trimmed = input.trim();
            return withoutColonPattern.test(trimmed) && trimmed.length >= MIN_SEARCH_LENGTH;
        }
    }

    const getRouteByType = (role: string, id: string, user_id: string) => {
        switch (role) {
            case 'campaign':
                return `/campaigns/${id}`;
            case 'ntaf':
                return `/campaigns/${id}`;
            case 'campaign_request':
                return `/campaign-requests/${id}`;
            case 'medical professional':
                return `/medical-professionals/${id}`;
            case 'medical_center':
                return `/medical-centers/${id}`;
            default:
                return `/users/${user_id}`;
        }
    };

    const customStyles = {
        control: (provided: any) => ({
            ...provided,
            border: 'none',
            boxShadow: 'none',
            background: 'transparent',
        }),
        input: (provided: any) => ({
            ...provided,
        }),
        container: (provided: any) => ({
            ...provided,
            width: '300px',
        }),
        noOptionsMessage: (provided: any) => ({
            ...provided,
        }),
        menu: (provided) => ({
            ...provided,
            marginTop: '22px',
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '560px',
        }),
    };

    return (
        <header className='sticky top-0 z-999 flex w-full bg-white dark:bg-boxdark dark:drop-shadow-none'>
            <div className='flex flex-grow items-center justify-between px-4 py-4 md:px-6 2xl:px-11'>
                <div className='flex items-center gap-2 sm:gap-4 lg:hidden'>
                    {/* Hamburger Toggle BTN */}
                    <button
                        aria-controls='sidebar'
                        onClick={(e) => {
                            e.stopPropagation();
                            props.setSidebarOpen(!props.sidebarOpen);
                        }}
                        className='z-99999 block rounded-sm border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark lg:hidden'
                    >
                        <span className='relative block h-5.5 w-5.5 cursor-pointer'>
                            <span className='du-block absolute right-0 h-full w-full'>
                                <span
                                    className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-[0] duration-200 ease-in-out dark:bg-white ${
                                        !props.sidebarOpen &&
                                        '!w-full delay-300'
                                    }`}
                                ></span>
                                <span
                                    className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-150 duration-200 ease-in-out dark:bg-white ${
                                        !props.sidebarOpen &&
                                        'delay-400 !w-full'
                                    }`}
                                ></span>
                                <span
                                    className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-200 duration-200 ease-in-out dark:bg-white ${
                                        !props.sidebarOpen &&
                                        '!w-full delay-500'
                                    }`}
                                ></span>
                            </span>
                            <span className='absolute right-0 h-full w-full rotate-45'>
                                <span
                                    className={`absolute left-2.5 top-0 block h-full w-0.5 rounded-sm bg-black delay-300 duration-200 ease-in-out dark:bg-white ${
                                        !props.sidebarOpen && '!h-0 !delay-[0]'
                                    }`}
                                ></span>
                                <span
                                    className={`delay-400 absolute left-0 top-2.5 block h-0.5 w-full rounded-sm bg-black duration-200 ease-in-out dark:bg-white ${
                                        !props.sidebarOpen && '!h-0 !delay-200'
                                    }`}
                                ></span>
                            </span>
                        </span>
                    </button>
                    {/* Hamburger Toggle BTN */}

                    <Link className='block flex-shrink-0 lg:hidden' to='/'>
                        <img src={Logo} alt='Logo' style={{ width: '130px' }} />
                    </Link>
                </div>

                <div
                    className={`flex lg:w-full ${
                        showGlobalSearch ? 'justify-between' : 'justify-end'
                    }`}
                >
                    {showGlobalSearch && (
                    <div className='md:!w-full' style={{ width: '160px' }}>
                        <div>
                            <div className='flex'>
                                <button>
                                    <svg
                                        className='fill-body hover:fill-primary dark:fill-bodydark dark:hover:fill-primary'
                                        width='20'
                                        height='20'
                                        viewBox='0 0 20 20'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z'
                                            fill=''
                                        />
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z'
                                            fill=''
                                        />
                                    </svg>
                                </button>

                                <AsyncSelect
                                    className='text-xs md:text-base'
                                    loadOptions={loadOptions}
                                    filterOption={() => true}
                                    value=''
                                    components={{
                                        DropdownIndicator: null,
                                        IndicatorSeparator: null,
                                    }}
                                    onChange={(option) => {
                                        if (option) {
                                            Navigate(option?.value);
                                        }
                                    }}
                                    styles={customStyles}
                                    placeholder='Type to search...'
                                    noOptionsMessage={({ inputValue }) => {
                                        if (!isValidSearchString(inputValue)) {
                                            return <GlobalSearchInfo />;
                                        }
                                        return 'No options';
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    )}
                    <div className='flex items-center gap-3 2xsm:gap-7'>
                        {/* <div className='flex items-center gap-2 2xsm:gap-4'> */}
                        {/* <!-- Dark Mode Toggler --> */}
                        {/* <DarkModeSwitcher /> */}
                        {/* <!-- Dark Mode Toggler --> */}

                        {/* <!-- Notification Menu Area --> */}
                        {/* <DropdownNotification /> */}
                        {/* <!-- Notification Menu Area --> */}

                        {/* <!-- Chat Notification Area --> */}
                        {/* <DropdownMessage /> */}
                        {/* <!-- Chat Notification Area --> */}
                        {/* </div> */}

                        {/* <!-- User Area --> */}
                        <DropdownUser />
                        {/* <!-- User Area --> */}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
